import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@blueprintjs/core";
import Flexbox from "flexbox-react";
import EmptyStateNotice from "./EmptyStateNotice.js";
import FilledButton from "../buttons-20250224T111359Z-001/buttons/FilledButton.jsx";
import ContainerIntroBox from "./ContainerIntroBox.js";
import Cookies from "js-cookie";
import ToastStore from "../../stores/ToastStore.js";

const AcknowledgementTab = ({ goalSettingId }) => {
  const [loading, setLoading] = useState(true);
  const [submittedData, setSubmittedData] = useState(null);

  const [signatures, setSignatures] = useState({
    employeeSign: "",
    employeeDate: "",
    evaluator1Sign: "",
    evaluator1Date: "",
    evaluator2Sign: "",
    evaluator2Date: ""
  });

  useEffect(() => {
    if (goalSettingId) {
      fetchAcknowledgement();
    } else {
      setLoading(false);
    }
  }, [goalSettingId]);

  const fetchAcknowledgement = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${Cookies.get("apiEnv")}/assessments/review_acknowledgement?goal_setting_id=${goalSettingId}`
      );

      if (res.data && res.data.assessment_value?.length > 0) {
        setSubmittedData(res.data);
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleSignatureChange = (key, value) => {
    setSignatures((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async () => {
    const hasEmptyField = Object.values(signatures).some(
      (val) => !val?.trim()
    );

    if (hasEmptyField) {
      ToastStore.showToast("Please fill all required fields", "warning");
      return;
    }

    const payload = {
      goal_setting_id: goalSettingId,
      answer_type: "ShortAnswer",
      assessment_value: [
        { question: "Employee Signature", answer: signatures.employeeSign },
        { question: "Employee Date", answer: signatures.employeeDate },
        { question: "Evaluator 1 Signature", answer: signatures.evaluator1Sign },
        { question: "Evaluator 1 Date", answer: signatures.evaluator1Date },
        { question: "Evaluator 2 Signature", answer: signatures.evaluator2Sign },
        { question: "Evaluator 2 Date", answer: signatures.evaluator2Date }
      ]
    };

    try {
      await axios.post(
        `${Cookies.get("apiEnv")}/assessments/review_acknowledgement`,
        payload
      );

      await fetchAcknowledgement();

      ToastStore.showToast(
        "Acknowledgement submitted successfully",
        "success"
      );
    } catch (err) {
      ToastStore.showToast(
        "Something went wrong in submission",
        "danger"
      );
      console.error(err);
    }
  };

  const getValue = (label) => {
    return (
      submittedData?.assessment_value?.find((i) => i.question === label)
        ?.answer || ""
    );
  };

  if (loading) {
    return (
      <Flexbox justifyContent="center" style={{ marginTop: "100px" }}>
        Loading...
      </Flexbox>
    );
  }

  if (!goalSettingId) {
    return (
      <Flexbox className="self-assessment-container">
        <Card className="assessment-answer-container">
          <EmptyStateNotice
            title="No acknowledgement available"
            description="This section is not configured for this review cycle."
          />
        </Card>
      </Flexbox>
    );
  }

  const isReadOnly = submittedData?.assessment_value?.length > 0;

  const lineInputStyle = {
    border: "none",
    borderBottom: "1px solid #000",
    outline: "none",
    padding: "6px 4px",
    fontSize: "14px",
    background: "transparent"
  };

  return (
    <Flexbox flexDirection="column" gap="20px">
      <Flexbox className="self-assessment-container" gap="20px">
        <ContainerIntroBox
          iconType="acknowledgement"
          text="Acknowledgement"
          description="Please review and confirm your evaluation."
        />

        <Card
          className="assessment-answer-container"
          style={{ padding: "40px" }}
        >
          {isReadOnly ? (
            //READ ONLY MODE
            <Flexbox flexDirection="column" gap="30px">

              {/* Employee */}
              <Flexbox display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap="20px">
                <Flexbox alignItems="center" gap="10px" style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>
                    Employee Signature
                  </span>
                  <div style={lineInputStyle}>
                    {getValue("Employee Signature") || "-"}
                  </div>
                </Flexbox>

                <Flexbox alignItems="center" gap="10px" style={{ width: "150px" }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <div style={lineInputStyle}>
                    {getValue("Employee Date") || "-"}
                  </div>
                </Flexbox>
              </Flexbox>

              {/* Statement */}
              <p style={{ fontStyle: "italic", color: "#555", lineHeight: "1.5" }}>
                I have read this evaluation and it has been discussed with me.
                I understand that signing this evaluation does not necessarily
                mean that I agree with all of the information in it.
              </p>

              {/* Evaluator 1 */}
              <Flexbox alignItems="center" justifyContent="space-between" gap="20px" className="push-20-t push-20-b">
                <Flexbox alignItems="center" gap="10px" style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>
                    Evaluator 1 Signature
                  </span>
                  <div style={lineInputStyle}>
                    {getValue("Evaluator 1 Signature") || "-"}
                  </div>
                </Flexbox>

                <Flexbox alignItems="center" gap="10px" style={{ width: "150px" }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <div style={lineInputStyle}>
                    {getValue("Evaluator 1 Date") || "-"}
                  </div>
                </Flexbox>
              </Flexbox>

              {/* Evaluator 2 */}
              <Flexbox alignItems="center" justifyContent="space-between" gap="20px">
                <Flexbox alignItems="center" gap="10px" style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>
                    Evaluator 2 Signature
                  </span>
                  <div style={lineInputStyle}>
                    {getValue("Evaluator 2 Signature") || "-"}
                  </div>
                </Flexbox>

                <Flexbox alignItems="center" gap="10px" style={{ width: "150px" }}>
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <div style={lineInputStyle}>
                    {getValue("Evaluator 2 Date") || "-"}
                  </div>
                </Flexbox>
              </Flexbox>

            </Flexbox>
          ) : (
            // FORM MODE
            <Flexbox flexDirection="column" gap="30px">
              {/* Employee */}
              <Flexbox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap="15px" flexWrap="wrap">
                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Employee Signature</span>
                  <input
                    style={{ ...lineInputStyle, width: "260px" }}
                    value={signatures.employeeSign}
                    onChange={(e) =>
                      handleSignatureChange("employeeSign", e.target.value)
                    }
                  />
                </div>

                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <input
                    style={{ ...lineInputStyle, width: "140px" }}
                    placeholder="DD/MM/YYYY"
                    value={signatures.employeeDate}
                    onChange={(e) =>
                      handleSignatureChange("employeeDate", e.target.value)
                    }
                  />
                </div>
              </Flexbox>

              <Flexbox clasName="push-20-t">
                <p style={{ fontStyle: "italic", color: "#555", lineHeight: "1.5" }}>
                  I have read this evaluation and it has been discussed with me.
                  I understand that signing this evaluation does not necessarily
                  mean that I agree with all of the information in it.
                </p>
              </Flexbox>

              {/* Evaluator 1 */}
              <Flexbox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap="15px" flexWrap="wrap" className="push-20-b push-20-t" >
                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Evaluator 1 Signature</span>
                  <input
                    style={{ ...lineInputStyle, width: "260px" }}
                    value={signatures.evaluator1Sign}
                    onChange={(e) =>
                      handleSignatureChange("evaluator1Sign", e.target.value)
                    }
                  />
                </div>
                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <input
                    style={{ ...lineInputStyle, width: "140px" }}
                    placeholder="DD/MM/YYYY"
                    value={signatures.evaluator1Date}
                    onChange={(e) =>
                      handleSignatureChange("evaluator1Date", e.target.value)
                    }
                  />
                </div>
              </Flexbox>

              {/* Evaluator 2 */}
              <Flexbox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap="15px" flexWrap="wrap" style={{ marginTop: "5px" }}>
                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Evaluator 2 Signature</span>
                  <input
                    style={{ ...lineInputStyle, width: "260px" }}
                    value={signatures.evaluator2Sign}
                    onChange={(e) =>
                      handleSignatureChange("evaluator2Sign", e.target.value)
                    }
                  />
                </div>
                <div alignItems="center">
                  <span style={{ fontWeight: 500, fontSize: "16px" }}>Date</span>
                  <input
                    style={{ ...lineInputStyle, width: "140px" }}
                    placeholder="DD/MM/YYYY"
                    value={signatures.evaluator2Date}
                    onChange={(e) =>
                      handleSignatureChange("evaluator2Date", e.target.value)
                    }
                  />
                </div>
              </Flexbox>

              <div style={{ marginTop: "40px" }}>
                <FilledButton
                  text="Submit Acknowledgement"
                  onClick={handleSubmit}
                />
              </div>
            </Flexbox>
          )}
        </Card>
      </Flexbox>
    </Flexbox>
  );
};

export default AcknowledgementTab;